import { ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Spin } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
    exchangeMiniProgramMapTicket,
    getMiniProgramMapDistribution,
} from './miniProgramMapService'
import type { DeviceDistributionItem } from '../../types/device-mosquito'
import { loadTiandituApi } from '../../utils/tianditu'
import './MpMap.css'

interface TiandituLngLat {
    lng: number
    lat: number
}

type TiandituMarker = Record<string, unknown>
type TiandituLabel = Record<string, unknown>
type TiandituPoint = unknown

interface TiandituMap {
    centerAndZoom(point: TiandituLngLat, zoom: number): void
    addOverLay(overlay: TiandituMarker | TiandituLabel): void
    removeOverLay(overlay: TiandituMarker | TiandituLabel): void
    addControl?(control: unknown): void
    clearOverLays?(): void
    enableDrag?(): void
    enableScrollWheelZoom?(): void
    enableDoubleClickZoom?(): void
    enableKeyboard?(): void
}

interface TiandituNamespace {
    Map: new (container: HTMLDivElement) => TiandituMap
    LngLat: new (lng: number, lat: number) => TiandituLngLat
    Marker: new (point: TiandituLngLat) => TiandituMarker
    Label?: new (options: {
        text: string
        position: TiandituLngLat
        offset?: TiandituPoint
    }) => TiandituLabel
    Point?: new (x: number, y: number) => TiandituPoint
    Control?: {
        Zoom?: new () => unknown
        MapType?: new () => unknown
    }
}


interface MarkerOverlay {
    marker: TiandituMarker
    label?: TiandituLabel
}

type PageStatus = 'loading' | 'ready' | 'empty' | 'error'

const DEFAULT_CENTER = { lng: 113.75793, lat: 23.01781 }
const DEFAULT_ZOOM = 10
const MAX_VISIBLE_DEVICES = 200
const MAX_VISIBLE_MARKERS = 300
const MAP_TICKET_EXPIRED_MESSAGE = '小程序登录状态已过期，请返回重新进入'

const getTianditu = (): TiandituNamespace | undefined =>
    (window as Window & { T?: TiandituNamespace }).T

const readMapTicket = (): string => {
    if (typeof window === 'undefined') {
        return ''
    }

    return new URLSearchParams(window.location.search).get('ticket')?.trim() ?? ''
}

const getHttpStatusCode = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } })?.response?.status

const getMapErrorMessage = (error: unknown, usesTicket: boolean): string => {
    const statusCode = getHttpStatusCode(error)
    if (usesTicket && (statusCode === 401 || statusCode === 403)) {
        return MAP_TICKET_EXPIRED_MESSAGE
    }
    return error instanceof Error ? error.message : '地图加载失败'
}

const hasValidCoordinates = (item: DeviceDistributionItem): boolean =>
    Number.isFinite(item.lng) && Number.isFinite(item.lat)

const buildMarkerLabel = (item: DeviceDistributionItem): string => {
    if (item.type === 'device') {
        return `${item.label} · 已灭蚊 ${item.total_count} 只`
    }

    return `${item.label} · ${item.device_count} 台 · ${item.total_count} 只`
}

const getDisplayItems = (items: DeviceDistributionItem[]): DeviceDistributionItem[] =>
    items.filter(hasValidCoordinates).slice(0, MAX_VISIBLE_MARKERS)

const enableMapInteractions = (map: TiandituMap): void => {
    map.enableDrag?.()
    map.enableScrollWheelZoom?.()
    map.enableDoubleClickZoom?.()
    map.enableKeyboard?.()
}

const MpMap: React.FC = () => {
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<TiandituMap | null>(null)
    const markerOverlaysRef = useRef<MarkerOverlay[]>([])
    const [status, setStatus] = useState<PageStatus>('loading')
    const [errorMessage, setErrorMessage] = useState('')
    const [items, setItems] = useState<DeviceDistributionItem[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [deviceCount, setDeviceCount] = useState(0)

    const clearMarkerOverlays = useCallback(() => {
        const map = mapInstanceRef.current
        if (!map) {
            markerOverlaysRef.current = []
            return
        }

        markerOverlaysRef.current.forEach((overlay) => {
            map.removeOverLay(overlay.marker)
            if (overlay.label) {
                map.removeOverLay(overlay.label)
            }
        })
        markerOverlaysRef.current = []
    }, [])

    const renderMarkers = useCallback((map: TiandituMap, nextItems: DeviceDistributionItem[]) => {
        const tianditu = getTianditu()
        if (!tianditu) {
            return
        }

        clearMarkerOverlays()
        const overlays: MarkerOverlay[] = []

        nextItems.forEach((item) => {
            const point = new tianditu.LngLat(item.lng, item.lat)
            const marker = new tianditu.Marker(point)
            map.addOverLay(marker)

            let label: TiandituLabel | undefined
            if (tianditu.Label) {
                label = new tianditu.Label({
                    text: buildMarkerLabel(item),
                    position: point,
                    offset: tianditu.Point ? new tianditu.Point(8, -28) : undefined,
                })
                map.addOverLay(label)
            }

            overlays.push({ marker, label })
        })

        markerOverlaysRef.current = overlays
    }, [clearMarkerOverlays])

    const initializeMap = useCallback(async () => {
        setStatus('loading')
        setErrorMessage('')

        try {
            await loadTiandituApi()
            const tianditu = getTianditu()
            const mapContainer = mapContainerRef.current
            if (!tianditu || !mapContainer) {
                throw new Error('天地图初始化失败')
            }

            mapContainer.innerHTML = ''
            const map = new tianditu.Map(mapContainer)
            map.centerAndZoom(new tianditu.LngLat(DEFAULT_CENTER.lng, DEFAULT_CENTER.lat), DEFAULT_ZOOM)
            if (tianditu.Control?.Zoom) {
                map.addControl?.(new tianditu.Control.Zoom())
            }
            if (tianditu.Control?.MapType) {
                map.addControl?.(new tianditu.Control.MapType())
            }
            enableMapInteractions(map)
            mapInstanceRef.current = map

            const ticket = readMapTicket()
            let mapAccessToken: string | undefined
            if (ticket) {
                mapAccessToken = await exchangeMiniProgramMapTicket(ticket)
            }

            const response = await getMiniProgramMapDistribution(
                { zoom: DEFAULT_ZOOM, maxVisibleDevices: MAX_VISIBLE_DEVICES },
                mapAccessToken
            )
            const displayItems = getDisplayItems(response.items)
            setItems(displayItems)
            setTotalCount(response.meta.total_count)
            setDeviceCount(response.meta.device_count)

            if (displayItems.length === 0) {
                setStatus('empty')
                return
            }

            const firstItem = displayItems[0]
            map.centerAndZoom(new tianditu.LngLat(firstItem.lng, firstItem.lat), DEFAULT_ZOOM)
            renderMarkers(map, displayItems)
            setStatus('ready')
        } catch (error) {
            setErrorMessage(getMapErrorMessage(error, Boolean(readMapTicket())))
            setStatus('error')
        }
    }, [renderMarkers])

    useEffect(() => {
        const mapContainer = mapContainerRef.current
        void initializeMap()

        return () => {
            clearMarkerOverlays()
            markerOverlaysRef.current = []
            mapInstanceRef.current = null
            if (mapContainer) {
                mapContainer.innerHTML = ''
            }
        }
    }, [clearMarkerOverlays, initializeMap])

    const showOverlay = status !== 'ready'

    return (
        <main className="mp-map-page" aria-label="小程序天地图设备分布">
            <section className="mp-map-summary" aria-label="设备分布摘要">
                <div>
                    <span className="mp-map-summary__label">设备数量</span>
                    <strong>{deviceCount}</strong>
                </div>
                <div>
                    <span className="mp-map-summary__label">累计灭蚊</span>
                    <strong>{totalCount}</strong>
                </div>
                <div>
                    <span className="mp-map-summary__label">可见点位</span>
                    <strong>{items.length}</strong>
                </div>
            </section>

            <section className="mp-map-canvas" aria-label="天地图点位地图">
                <div ref={mapContainerRef} className="mp-map-canvas__container" />
                {showOverlay && (
                    <div className="mp-map-canvas__overlay" role="status">
                        {status === 'loading' && (
                            <div className="mp-map-loading">
                                <Spin />
                                <span>地图加载中...</span>
                            </div>
                        )}
                        {status === 'empty' && (
                            <Alert
                                type="info"
                                showIcon
                                message="暂无可展示的设备点位"
                                description="请确认当前账号可见范围内的设备已维护经纬度。"
                            />
                        )}
                        {status === 'error' && (
                            <Alert
                                type="error"
                                showIcon
                                message="地图加载失败"
                                description={errorMessage || '请稍后重试，或确认天地图配置和设备分布权限。'}
                                action={
                                    <Button
                                        size="small"
                                        icon={<ReloadOutlined />}
                                        onClick={() => void initializeMap()}
                                    >
                                        重试
                                    </Button>
                                }
                            />
                        )}
                    </div>
                )}
            </section>
        </main>
    )
}

export default MpMap
